import { Injectable, OnDestroy } from '@angular/core'
import {
  combineLatest,
  merge,
  Observable,
  of,
  Subject,
  throwError,
  timer,
} from 'rxjs'
import {
  catchError,
  filter,
  map,
  pairwise,
  retry,
  share,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators'
import {
  ActionDropTagChoices,
  ActionFinalCancel,
  ActionFinalConfirm,
  ActionModeChoice,
  ActionTagChoice,
  MachineState,
  MachineStateTransport,
} from '../models/states.models'

import {
  ComputedRecord,
  MachineStateComputed,
} from '../models/states-computed.models'
import { TouaregControlStateActionsService } from './touareg-control-state-actions.service'
import {
  TouaregControlStateComputeService,
  UIControllerList,
} from './touareg-control-state-compute.service'
import { TouaregControlStateParamsService } from './touareg-control-state-params.service'
// import { Apollo } from 'apollo-angular'
//import { SUBSCRIBE_TOUAREG_CONTROLLER_STATE } from '../queries/touareg-control.queries'
import { HttpClient } from '@angular/common/http'
import dayjs from 'dayjs'
// import { AppStateService } from '@/service/app/app-state.service'
//import { Tag } from '../../commons/models/tag.model'
//import { NetworkService } from '@/shared/services/network/network.service'
//import { ErrorMessageService } from '@/shared/services/error-message/error-message.service'

import {
  ITouaregOperatorActionDto,
  TagsChoice,
  TagClient,
  TouaregOperatorActionDto,
} from '@/shared/services/service-clients'
import moment, { Moment } from 'moment'
@Injectable()
export class TouaregControlStateMachineService implements OnDestroy {
  private readonly _directComputation = new Subject<MachineStateComputed>()

  // Déclaration de l'observable de la machine à état
  readonly fromDirectComputation$: Observable<MachineStateComputed> =
    this._directComputation.asObservable()
  readonly purefromStateMachine$: Observable<MachineStateTransport | null>
  readonly fromStateMachine$: Observable<MachineStateComputed | null>
  readonly stateMachine$: Observable<MachineStateComputed | null>

  testIsStop: boolean = false
  private _currentState: MachineStateComputed | undefined

  constructor(
    private _http: HttpClient,
    // private _apollo: Apollo,
    // private _network: NetworkService,
    // private _appState: AppStateService,
    private _actions: TouaregControlStateActionsService,
    private _compute: TouaregControlStateComputeService,
    private _params: TouaregControlStateParamsService,
    private _tagProxy: TagClient //private _errorMessageService: ErrorMessageService
  ) {
    // Creation de l'observable de la machine à état
    // Dépend de l'activation dans les global settings,
    // du beaconId, du boolean de connexion,
    // de l'état du network, de la connexion à la machine à état du touareg
    this.purefromStateMachine$ = combineLatest([
      this._params.params$,
      // this._network.networkChange$,
      // this._appState.globalSettings$,
    ]).pipe(
      switchMap(([params /*ping*/ /* settings */]) => {
        // On écoute l'état réseau, et s'il est mauvais
        // On coupe tout
        // console.log('Santé ===> ', ping?.network)
        // if (!ping?.network) {
        //   return of(null)
        // }

        // On check si le touareg controller est activé dans les global settings
        // Si non activé, on stoppe tout
        //const key = 'umap_features.json'
        /*         if (!settings[key]?.touaregController) {
          return of(null)
        } */

        const { active, beaconId, connect } = params
        // Si la conexion est activée dans les paramètres
        // Et qu'une beacon cible est renseignée dans les paramètres
        if (active && connect && beaconId) {
          // Alors on établit la connexion avec la table dm de la machine
          console.log('CONNECT TOUAREG....')
          return this._connect(beaconId)
        }
        console.log('DISCONNECT TOUAREG.')
        // Sinon on renvoie null.
        // Cela déclenche le reset et nettoyage de la UI
        // dans l'évaluation de 'this._clearIfSessionChanged'
        return of(null)
      }),
      share()
    )

    // Creation de l'observable de traitement d'état
    this.fromStateMachine$ = this.purefromStateMachine$.pipe(
      // On forme une paire avec la précédente valeur émise
      // et la valeur nouvellement émise.
      // Permet de déterminer s'il y a un changement de la machine :
      //  => Changement de session
      //  => Changement de state
      // Dans ces cas là, on reset la UI et les caches pour coller à l'état machine courant
      pairwise(),
      // 1 - on analyse la latence entre les deux émissions
      // tap((data) => this._monitor.analyse(data)),
      // 2 - on nettoie l'état UI si besoin
      tap((data) => this._clearIfAnyChanges(data)),
      // On poursuit avec la dernière data émise
      map(([, curr]) => curr?.content as MachineState),
      // A partir d'ici, on met à jour les actions
      tap((data) => this._actions.resolve(data)),
      // Et on calcule et on met à jour les nouvelles listes
      map((data) => this._compute.computeStateFromMachine(data)),
      // On stocke le dernier état machine
      tap((data) => (this._currentState = data ?? undefined))
    )

    // Creation de l'observable final de la machine à état
    // qui est issu de l'observation directe de la machine
    // et de la creation directe de l'état
    this.stateMachine$ = merge(
      this.fromStateMachine$,
      this.fromDirectComputation$
    ).pipe(
      //filter(() => !this.testIsStop),
      shareReplay(1)
    )
  }

  private _connect(beaconId: number): Observable<MachineStateTransport> {
    // Le now nous permet de stocker la date d'envoie de la requete
    let now: Moment | null = null
    const polling$ = timer(2000, 1000).pipe(
      switchMap(() => {
        // On stocke la date d'envoie de la requete
        now = moment()
        return this._tagProxy.obtenirTouareg(beaconId)
      }),

      map((o) => {
        // system_time => time sur le serveur geoxp back
        // bfs_time => time sur le serveur bfs
        const { data, bfs_time } = o ?? {}

        // Si pas de données, on emet aucun nouvel etat
        if (
          !data?.trace_touareg_controller_state_dm ||
          !data.trace_touareg_controller_state_dm[0]
        ) {
          return undefined
        }

        // On déduit le ping serveur geoxp
        const recept = moment()
        const ping = recept.diff(now)

        // On calcule le décalage d'horloge avec le serveur geoxp
        // Todo => récupérer le décalage avec le serveur BFS
        const dateOffset = recept.diff(bfs_time as unknown as moment.MomentInput)

        // On récupère la machine state
        const content = data.trace_touareg_controller_state_dm[0]
          .content as unknown as MachineState

        // On créé un machine state transport
        const state: MachineStateTransport = {
          content,
          ping,
          dateOffset,
        }

        return state
      }),
      // On ne propage que les états effectivement calculés
      filter((s): s is MachineStateTransport => !!s),
      retry()
    )

    return polling$
  }

  private _actionQuery(
    action:
      | ActionTagChoice
      | ActionFinalCancel
      | ActionFinalConfirm
      | ActionModeChoice
      | ActionDropTagChoices
  ) {
    // Horodatage de la requete
    action['choice_update_date'] = dayjs().format()
    let input = {
      ...(action as any),
    } as ITouaregOperatorActionDto
    let actionTouareg = new TouaregOperatorActionDto(input)
    if (actionTouareg?.tags_choices?.length) {
      let instanceTagsChoices = actionTouareg.tags_choices.map(
        (tagChoice: TagsChoice) => new TagsChoice(tagChoice)
      )
      actionTouareg.tags_choices = instanceTagsChoices
    }

    return this._tagProxy.creerTouaregActionOperateur(actionTouareg).pipe(
      catchError((e) => {
        //this._errorMessageService.pushAllErrors(e)
        console.error(e)
        this._actions.setError(action.action_uid)
        const computed = this._compute.reCompute()
        if (computed) this._directComputation.next(computed)
        return throwError(e)
      })
    )
  }

  private _checkHasSessionChanged(states: (MachineStateTransport | null)[]) {
    return (
      states[0]?.content?.state_machine_session_uid !==
      states[1]?.content?.state_machine_session_uid
    )
  }

  private _checkHasStateChanged(states: (MachineStateTransport | null)[]) {
    return states[0]?.content?.state !== states[1]?.content?.state
  }

  private _clearIfAnyChanges(states: (MachineStateTransport | null)[]) {
    if (!states[1]) {
      this._actions.clear()
      this._compute.clear()
    } else if (this._checkHasStateChanged(states)) {
      this._actions.clear()
      this._compute.clear()
    } else if (this._checkHasSessionChanged(states)) {
      this._actions.clear()
      this._compute.clear()
    }
  }

  public stop() {
    this.testIsStop = !this.testIsStop
  }

  public restore() {
    const computed = this._compute.restore()
    if (computed) this._directComputation.next(computed)
  }

  public setModeManual(): Observable<boolean> {
    const { action, state$ } = this._actions.makeModeManual(this._currentState)
    this._actionQuery(action).subscribe()
    return state$
  }

  public setModeAuto(): Observable<boolean> {
    const { action, state$ } = this._actions.makeModeAuto(this._currentState)
    this._actionQuery(action).subscribe()
    return state$
  }

  public setModeLock(): Observable<boolean> {
    const { action, state$ } = this._actions.makeModeLock(this._currentState)
    this._actionQuery(action).subscribe()
    return state$
  }

  public actionFinalConfirm(): Observable<boolean> {
    const { action, state$ } = this._actions.makeFinalConfirm(
      this._currentState
    )
    this._actionQuery(action).subscribe()
    return state$
  }

  public actionFinalCancel(): Observable<boolean> {
    const { action, state$ } = this._actions.makeFinalCancel(this._currentState)
    this._actionQuery(action).subscribe()
    return state$
  }

  public actionPickTag(tags: ComputedRecord[]): Observable<boolean> {
    // Creation de l'action
    const records = tags.map((t) => t.record)
    const { action, state$ } = this._actions.makePickTag(
      records,
      this._currentState
    )

    // Creation de l'état UI temporaire
    const computed = this._compute.localSwitchAndCompute(
      records,
      UIControllerList.selection
    )
    // Appel de l'action
    this._actionQuery(action).subscribe()
    // Emission de la valeur d'état UI temporaire
    if (computed) this._directComputation.next(computed)
    return state$
  }

  public actionRejectTag(tags: ComputedRecord[]): Observable<boolean> {
    // Creation de l'action
    const records = tags.map((t) => t.record)
    const { action, state$ } = this._actions.makeRejectTag(
      records,
      this._currentState
    )
    // Creation de l'état UI temporaire
    const computed = this._compute.localSwitchAndCompute(
      records,
      UIControllerList.suggestion
    )
    // Appel de l'action
    this._actionQuery(action).subscribe()
    // Emission de la valeur d'état UI temporaire
    if (computed) this._directComputation.next(computed)
    return state$
  }

  public actionDropTagChoices(tags: ComputedRecord[]): Observable<boolean> {
    // Creation de l'action
    const records = tags.map((t) => t.record)
    const { action, state$ } = this._actions.makeDropTagChoices(
      records,
      this._currentState
    )

    // Creation de l'état UI temporaire
    const computed = this._compute.localSwitchAndCompute(
      records,
      UIControllerList.selection
    )
    // Appel de l'action
    this._actionQuery(action).subscribe()
    // Emission de la valeur d'état UI temporaire
    if (computed) this._directComputation.next(computed)
    return state$
  }

  public actionManualDropTagChoices(tags: any[]): Observable<boolean> {
    // Creation de l'action
    const { action, state$ } = this._actions.makeManualDropTagChoices(
      tags,
      this._currentState
    )

    // Creation de l'état UI temporaire
    const computed = this._compute.localAddAndCompute(
      action.tags_choices,
      UIControllerList.selection
    )
    // Appel de l'action
    this._actionQuery(action).subscribe()
    // Emission de la valeur d'état UI temporaire
    if (computed) this._directComputation.next(computed)
    return state$
  }

  public actionDropTagReject(tags: ComputedRecord[]): Observable<boolean> {
    // Creation de l'action
    const records = tags.map((t) => t.record)
    const { action, state$ } = this._actions.makeDropTagReject(
      records,
      this._currentState
    )

    // Creation de l'état UI temporaire
    const computed = this._compute.localSwitchAndCompute(
      records,
      UIControllerList.suggestion
    )
    // Appel de l'action
    this._actionQuery(action).subscribe()
    // Emission de la valeur d'état UI temporaire
    if (computed) this._directComputation.next(computed)
    return state$
  }

  public actionManualPickTag(tags: any[]): Observable<boolean> {
    // Creation de l'action
    const { action, state$ } = this._actions.makeManualPickTag(
      tags,
      this._currentState
    )
    // Creation de l'état UI temporaire
    const computed = this._compute.localAddAndCompute(
      action.tags_choices,
      UIControllerList.selection
    )
    // Appel de l'action
    this._actionQuery(action).subscribe()
    // Emission de la valeur d'état UI temporaire
    if (computed) this._directComputation.next(computed)
    return state$
  }

  ngOnDestroy() {}
}
